import { FunctionComponent, HTMLAttributes, ButtonHTMLAttributes, HeadingHTMLAttributes } from 'preact';
import { useConfig } from '../providers/ConfigProvider';
import { useUnifiedWidget } from '../providers/UnifiedWidgetProvider';
import { ChatBot } from '../icons/Chat';
import VoiceIcon from '../icons/Voice';
import WhatsAppIcon from '../icons/WhatsApp';
import { PoweredBySA } from './PoweredBy';

type MenuContainerProps = HTMLAttributes<HTMLDivElement> & {
  open: boolean;
  position: string;
  width: number;
}
const MenuContainer = ({
  open,
  position,
  width,
  children,
  ...props
}: MenuContainerProps) => {
  const { gooberCss } = useUnifiedWidget();

  return (
    <div
      {...props}
      className={gooberCss({
        position: "absolute",
        bottom: 0,
        width: `${width}px`,
        maxWidth: open ? "calc(100vw - 30px)" : "0px",
        right: position === "bottom-right" ? '0' : undefined,
        left: position === "bottom-left" ? '0' : undefined,
        borderRadius: "16px",
        overflow: "hidden",
        boxShadow: "0 6px 24px rgba(0, 0, 0, 0.16)",
        opacity: open ? 1 : 0,
        transform: open ? "translateY(0) scale(1)" : "translateY(30px) scale(0.7)",
        transition: open
          ? "opacity 0.15s ease-out, transform 0.2s cubic-bezier(0.20, 1.35, 0.22, 1)"
          : "opacity 0.1s ease-in, transform 0.1s ease-in",
        pointerEvents: open ? "auto" : "none",
        background: '#ffffff',
      })}
    >
      {children}
    </div>
  )
};

const MenuContent = ({ children, ...props }: HTMLAttributes<HTMLDivElement>) => {
  const { gooberCss } = useUnifiedWidget();
  return (
    <div {...props} className={gooberCss({
      padding: "24px",
      display: "flex",
      flexDirection: "column",
    })}>
      {children}
    </div>
  )
}

const MenuTitle = ({ children, ...props }: HeadingHTMLAttributes<HTMLHeadingElement>) => {
  const { gooberCss } = useUnifiedWidget();
  return (
    <h3 {...props} className={gooberCss({
      margin: 0,
      fontSize: "20px",
      fontWeight: 600,
      textAlign: "center",
      marginBottom: "6px",
    })}>
      {children}
    </h3>
  )
}

const MenuSubtitle = ({ children, ...props }: HTMLAttributes<HTMLParagraphElement>) => {
  const { gooberCss } = useUnifiedWidget();
  return (
    <p {...props} className={gooberCss({
      margin: 0,
      fontSize: "14px",
      textAlign: "center",
      opacity: 0.8,
      marginBottom: "6px",
    })}>
      {children}
    </p>
  )
}

type ModeButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  color: string;
  buttonColor: string;
}
const ModeButton = ({ color, buttonColor, children, ...props }: ModeButtonProps) => {
  const { gooberCss } = useUnifiedWidget();
  return (
    <button {...props} className={gooberCss({
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "12px",
      padding: "14px 20px",
      borderRadius: "12px",
      fontSize: "16px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "transform 0.1s ease",
      border: "none",
      background: buttonColor,
      color: color,
      '&:hover': {
        transform: "scale(1.02)",
      },
      '&:active': {
        transform: "scale(0.98)",
      },
    })}>
      {children}
    </button>
  )
}

const ButtonText = ({ children, ...props }: HTMLAttributes<HTMLSpanElement>) => {
  const { gooberCss } = useUnifiedWidget();
  return (
    <span {...props} className={gooberCss({
      flex: 1,
      fontSize: "14px",
      textAlign: "left",
    })}>
      {children}
    </span>
  )
}

type ButtonDescriptionProps = HTMLAttributes<HTMLDivElement> & {
  color: string;
}
const ButtonDescription = ({ color, children, ...props }: ButtonDescriptionProps) => {
  const { gooberCss } = useUnifiedWidget();
  return (
    <div {...props} className={gooberCss({
      fontSize: "12px",
      fontWeight: 400,
      color: color,
      marginTop: "4px",
      opacity: 0.8,
    })}>
      {children}
    </div>
  )
}

export const ModeSelectionMenu: FunctionComponent = () => {
  const { config } = useConfig();
  const { gooberCss } = useUnifiedWidget();
  const { open, mode, setMode, agentInfo } = useUnifiedWidget();
  const { position, width, channels } = config;
  const whatsappNumber = agentInfo?.whatsapp_number;

  const isMenuMode = mode === 'menu';
  const showMenu = open && isMenuMode;

  return (
    <MenuContainer open={showMenu} position={position} width={width}>
      <MenuContent>
        { config.titleText && <MenuTitle>{config.titleText}</MenuTitle> }
        { config.subtitleText && <MenuSubtitle>{config.subtitleText}</MenuSubtitle> }
  
        <div className={gooberCss({
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          marginTop: "10px",
        })}>
          {channels.includes('web') && (
            <ModeButton
              onClick={() => setMode('chat')}
              color={config.buttonTextColor}
              buttonColor={config.buttonBgColor}
            >
              <ChatBot fill={config.buttonTextColor} width={28} height={28} />
              <ButtonText>
                {config.chatButtonText}
                <ButtonDescription color={config.buttonTextColor}>
                  {config.chatButtonDescriptionText}
                </ButtonDescription>
              </ButtonText>
            </ModeButton>
          )}

          {channels.includes('voice') && (
            <ModeButton
              onClick={() => setMode('voice')}
              color={config.buttonTextColor}
              buttonColor={config.buttonBgColor}
            >
              <VoiceIcon fill={config.buttonTextColor} width={28} height={28} />
              <ButtonText>
                {config.voiceButtonText}
                <ButtonDescription color={config.buttonTextColor}>
                  {config.voiceButtonDescriptionText}
                </ButtonDescription>
              </ButtonText>
            </ModeButton>
          )}

          {channels.includes('whatsapp') && whatsappNumber && (
            <ModeButton
              onClick={() => window.open(`https://wa.me/${whatsappNumber.replace(/\D/g, '')}`, '_blank', 'noopener,noreferrer')}
              color={config.buttonTextColor}
              buttonColor={config.buttonBgColor}
            >
              <WhatsAppIcon fill={config.buttonTextColor} width={26} height={26} />
              <ButtonText>
                {config.whatsappButtonText}
                <ButtonDescription color={config.buttonTextColor}>
                  {config.whatsappButtonDescriptionText}
                </ButtonDescription>
              </ButtonText>
            </ModeButton>
          )}
        </div>
        { !config.removeWatermark && <PoweredBySA /> }
      </MenuContent>
    </MenuContainer>
  );
};
